import {
  Home,
  MessageSquare,
  Users,
  Calendar,
  Settings,
  CreditCard,
  UserCog,
  BarChart3,
  Building2,
  List,
  ChevronRight,
  Info,
  Bell,
  LayoutGrid,
  History,
  Brain,
  Database,
  Bot,
  GraduationCap,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { useNavigationGuard } from "@/contexts/NavigationGuardContext";
import { useClinic } from "@/contexts/ClinicContext";
import { isPowerUser, isPlatformAdmin, isOrgUser, isOrgAdmin } from "@/types/auth";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  SidebarMenuSub,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { consultApi } from "@/api/consult";
import { useLearningInboxUnreadCount } from "@/hooks/useLearningInbox";

// Define menu item type
type MenuItem = {
  title: string;
  url: string;
  icon: React.ElementType;
  items?: {
    title: string;
    url: string;
    icon?: React.ElementType;
  }[];
};

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { user } = useAuthStore();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 아웃바운드 메뉴 feature flag (현재 주석처리됨)
  const { features } = useClinic();
  const orgUser = isOrgUser(user);
  const location = useLocation();
  const navigate = useNavigate();
  const { shouldBlockNavigation, showNavigationDialog } = useNavigationGuard();

  // State for collapsible menus
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  // 미처리 콜백 건수 조회 (1분마다 갱신)
  const { data: callbackSummary } = useQuery({
    queryKey: ["consults", "callback-pending-count"],
    queryFn: () => {
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      const toStr = today.toISOString().slice(0, 10);
      const fromStr = sixMonthsAgo.toISOString().slice(0, 10);
      return consultApi.getSummaryCounts({
        callbackStatus: "PENDING",
        from: fromStr,
        to: toStr,
      });
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const pendingCallbackCount = callbackSummary?.callbackNeeded ?? 0;

  // V1257: AI 학습함 미처리 건수 (작업 #4)
  const learningInboxCount = useLearningInboxUnreadCount();

  // Initialize open state based on current location
  useEffect(() => {
    const menuGroups = menuItems.filter(
      (item) => item.items && item.items.length > 0,
    );
    setOpenMenus((prev) => {
      const newState = { ...prev };
      for (const group of menuGroups) {
        if (location.pathname.startsWith(group.url)) {
          newState[group.url] = true;
        }
      }
      return newState;
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMenu = (url: string) => {
    setOpenMenus((prev) => ({ ...prev, [url]: !prev[url] }));
  };

  // 모든 사용자 접근 가능
  const commonMenuItems: MenuItem[] = [
    { title: "대시보드", url: "/dashboard", icon: Home },
    { title: "상담 이력", url: "/consults", icon: MessageSquare },
    {
      title: "예약 관리",
      url: "/appointments",
      icon: Calendar,
      items: [
        { title: "예약 현황", url: "/appointments/list", icon: List },
        {
          title: "예약 타임테이블",
          url: "/appointments/timetable",
          icon: LayoutGrid,
        },
        // V179: 예약상품은 설정 > 예약 관리로 이동됨 (중복 제거)
      ],
    },
  ];

  // V1255 (RBAC policy revision):
  //   - 운영 5개 메뉴(상담모드/병원정보/운영휴무/예약관리/지식관리): 모든 조직 사용자 노출
  //     (ORG_USER는 조회 전용, ORG_POWER+는 편집)
  //   - 거버넌스 3개 메뉴(변경이력/사용자관리/구독관리): ORG_ADMIN+ 전용
  const settingsSubItems = [
    { title: "상담 모드", url: "/settings/consultation-mode", icon: Bot }, // V1153
    { title: "병원 정보", url: "/settings/general", icon: Info },
    { title: "운영/휴무", url: "/settings/operation", icon: Settings },
    // V179: 예약 관리 (상품+설정 통합)
    { title: "예약 관리", url: "/settings/reservation", icon: Bell },
    // V1080: 고객 안내 제거됨 — KB(지식 관리)로 대체
    { title: "지식 관리", url: "/settings/knowledge-base", icon: Database }, // Knowledge Base
    // V1255: 변경 이력은 ORG_ADMIN+ 전용 (직원 간 분란 방지 audit 거버넌스)
    ...(isOrgAdmin(user) || isPlatformAdmin(user)
      ? [{ title: "변경 이력", url: "/settings/history", icon: History }]
      : []),
    // V1044: AI 분석 설정 - 플랫폼 관리자(SUPER_ADMIN, ADMIN)만 표시
    ...(isPlatformAdmin(user)
      ? [
          {
            title: "AI 분석 설정",
            url: "/settings/ai-analysis",
            icon: Brain,
          },
        ]
      : []),
    // V1173: 사용자 관리 / 구독 관리는 ORG_ADMIN+ 전용
    ...(isOrgAdmin(user) || isPlatformAdmin(user)
      ? [
          { title: "사용자 관리", url: "/user-management", icon: UserCog },
          { title: "구독 관리", url: "/subscription", icon: CreditCard },
        ]
      : []),
  ];

  const powerMenuItems: MenuItem[] = [
    { title: "고객 관리", url: "/customers", icon: Users },
    // V1257: AI 학습함 (작업 #4) — ORG_POWER+
    { title: "AI 학습함", url: "/learning-inbox", icon: GraduationCap },
    { title: "통계", url: "/stats", icon: BarChart3 },
    // V1255: 설정 메뉴는 모든 조직 사용자에게 노출 (ORG_USER는 페이지 진입 후 read-only 배너)
    {
      title: "설정",
      url: "/settings",
      icon: Settings,
      items: settingsSubItems,
    },
  ];

  // 플랫폼 관리자만 접근 가능 (SUPER_ADMIN, ADMIN, OPERATOR)
  const platformAdminMenuItems: MenuItem[] = [
    { title: "병원 관리", url: "/admin/clinics", icon: Building2 },
    { title: "플랫폼 사용자", url: "/admin/users", icon: UserCog },
  ];

  // 권한별 메뉴 구성
  let menuItems = [...commonMenuItems];

  // 플랫폼 관리자 (SUPER_ADMIN, ADMIN, OPERATOR)
  if (isPlatformAdmin(user)) {
    menuItems = [...menuItems, ...platformAdminMenuItems];
  }
  // 조직 사용자 (ORG_ADMIN, ORG_POWER, ORG_USER)
  else if (isPowerUser(user) || orgUser) {
    menuItems = [...menuItems, ...powerMenuItems];

    // 아웃바운드 메뉴는 feature flag에 따라 표시
    // if (features.outbound) {
    //   // "고객 관리" 다음에 삽입
    //   const customerIndex = menuItems.findIndex(item => item.url === "/customers");
    //   if (customerIndex !== -1) {
    //     menuItems.splice(customerIndex + 1, 0, {
    //       title: "아웃바운드",
    //       url: "/outbound-queue",
    //       icon: Phone
    //     });
    //   } else {
    //     menuItems.push({ title: "아웃바운드", url: "/outbound-queue", icon: Phone });
    //   }
    // }
  }

  const handleNavigation = (e: React.MouseEvent, url: string) => {
    // V1041: 모든 페이지에서 미저장 변경사항 있으면 경고
    // 같은 URL이면 차단하지 않음 (예: /settings/general → /settings/general)
    if (shouldBlockNavigation && !location.pathname.startsWith(url)) {
      e.preventDefault();
      showNavigationDialog(() => {
        navigate(url);
      });
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <div className="p-2 pt-7">
          <SidebarTrigger />
        </div>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="pt-2.5">
              {menuItems.map((item) => {
                const isActive = location.pathname.startsWith(item.url);
                const hasChildren = item.items && item.items.length > 0;
                const isOpen = openMenus[item.url];

                if (hasChildren) {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        onClick={() => {
                          toggleMenu(item.url);
                        }}
                        isActive={isActive}
                        className="justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <div className="relative flex-shrink-0">
                            <item.icon className="h-4 w-4" />
                          </div>
                          {!collapsed && <span>{item.title}</span>}
                        </div>
                        {!collapsed && (
                          <ChevronRight
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              isOpen && "rotate-90",
                            )}
                          />
                        )}
                      </SidebarMenuButton>
                      {isOpen && !collapsed && (
                        <SidebarMenuSub className="ml-0 border-l-0 px-0">
                          {item.items!.map((subItem) => (
                            <SidebarMenuItem key={subItem.title}>
                              <NavLink
                                to={subItem.url}
                                onClick={(e) =>
                                  handleNavigation(e, subItem.url)
                                }
                              >
                                {({ isActive: isSubActive }) => (
                                  <SidebarMenuSubButton
                                    isActive={isSubActive}
                                    className={cn(
                                      "pl-9 h-9",
                                      isSubActive
                                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                        : "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                                    )}
                                  >
                                    {subItem.icon && (
                                      <subItem.icon className="h-4 w-4 mr-2 opacity-70" />
                                    )}
                                    <span>{subItem.title}</span>
                                  </SidebarMenuSubButton>
                                )}
                              </NavLink>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  );
                }

                // 메뉴별 누적 배지 — 상담 이력(콜백) / AI 학습함(미처리)
                let badgeCount = 0;
                if (item.url === "/consults") {
                  badgeCount = pendingCallbackCount;
                } else if (item.url === "/learning-inbox") {
                  badgeCount = learningInboxCount;
                }
                const showBadge = badgeCount > 0;
                const badgeText = badgeCount > 99 ? "99+" : String(badgeCount);

                return (
                  <SidebarMenuItem key={item.title}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/dashboard"}
                      onClick={(e) => handleNavigation(e, item.url)}
                    >
                      {({ isActive }) => (
                        <SidebarMenuButton
                          className={cn(
                            "h-9 font-medium transition-all",
                            isActive
                              ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm hover:bg-sidebar-primary/90"
                              : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                          )}
                          isActive={isActive}
                        >
                          <div className="relative flex-shrink-0">
                            <item.icon className="h-4 w-4" />
                            {/* 누적 배지 (collapsed 시 아이콘에 표시) */}
                            {showBadge && collapsed && (
                              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-white leading-none">
                                {badgeText}
                              </span>
                            )}
                          </div>
                          {!collapsed && (
                            <>
                              <span className="flex-1">{item.title}</span>
                              {/* 누적 배지 (텍스트 옆에 표시) */}
                              {showBadge && (
                                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
                                  {badgeText}
                                </span>
                              )}
                            </>
                          )}
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
